import React from 'react';
import { FormikProps, FieldArray, FormikProvider } from 'formik';
import { Flex, ButtonV2, SpacingStack, Divider, TextV2 } from '@withjoy/joykit';
import { Contacts, Context } from '../../ContactCollectorGuest.controller';
import { Contacts as IframeContacts } from '@apps/contactCollector/iframe/ContactCollectorIframe.controller';
import { useFormController } from './Form.controller';
import { Wrapper, Footer } from '../components';
import { AddMember, ContactInfo, Header, MailingAddress, Tabs, TermsAndConditions } from './components';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';

interface FormProps
  extends Readonly<{
    formik: FormikProps<Contacts> | FormikProps<IframeContacts>;
    context?: Context;
    isCollectHouseholdEnabled: boolean;
    isIframeVersion?: boolean;
    isMutationLoading?: boolean;
    hasMutationError?: boolean;
    e164PhoneNumber: { [key: number]: string };
    setE164PhoneNumber: React.Dispatch<React.SetStateAction<{ [key: number]: string }>>;
  }> {}

export const Form: React.FC<FormProps> = ({
  formik,
  context,
  isCollectHouseholdEnabled,
  isIframeVersion = false,
  isMutationLoading,
  hasMutationError,
  e164PhoneNumber,
  setE164PhoneNumber
}) => {
  const {
    tAdmin,
    handleOnSaveDetailsClick,
    isFormValid,
    errors: { firstNameError, lastNameError, emailError, address1Error, postalCodeError, cityError, regionError, countryError },
    handleOnCountryDropdownChange,
    handleOnPhoneNumberChange,
    handleOnPhoneNumberFocus,
    isPhoneNumberInvalid,
    isEmailInvalid
  } = useFormController(formik, isIframeVersion, e164PhoneNumber, setE164PhoneNumber, context?.coupleNames);

  if (isMutationLoading) return <JoyLogoLoader loaderKey="contact-collector-form" />;

  return (
    <Wrapper>
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        paddingX={[isIframeVersion ? '40px' : '24px', null, 0]}
        width="100%"
        marginBottom={isIframeVersion ? '40px' : 0}
      >
        {!isIframeVersion && <Header title={tAdmin.addYourInfo()} coupleNames={context?.coupleNames} />}
        <FormikProvider key="contact-collector" value={formik}>
          <form style={{ width: '100%' }}>
            <FieldArray name="contacts">
              {({ push, remove }) => (
                <>
                  {isCollectHouseholdEnabled && <Tabs formik={formik} push={push} isIframeVersion={isIframeVersion} />}
                  {formik.values.contacts.map((contact, index) => (
                    <SpacingStack key={index} spacing={6}>
                      <ContactInfo
                        formik={formik}
                        index={index}
                        errors={{ firstNameError, lastNameError, emailError }}
                        isPhoneNumberInvalid={isPhoneNumberInvalid}
                        isEmailInvalid={isEmailInvalid}
                        handleOnPhoneNumberChange={handleOnPhoneNumberChange}
                        handleOnPhoneNumberFocus={handleOnPhoneNumberFocus}
                        remove={remove}
                        isIframeVersion={isIframeVersion}
                      />
                      {index === 0 && (
                        <MailingAddress
                          formik={formik}
                          index={index}
                          contact={contact}
                          handleOnCountryDropdownChange={handleOnCountryDropdownChange}
                          errors={{ address1Error, postalCodeError, cityError, regionError, countryError }}
                          isIframeVersion={isIframeVersion}
                        />
                      )}
                    </SpacingStack>
                  ))}
                  {isCollectHouseholdEnabled && (
                    <>
                      <Divider marginTop="2rem" />
                      <AddMember push={push} title={tAdmin.addMember()} isIframeVersion={isIframeVersion} />
                    </>
                  )}
                </>
              )}
            </FieldArray>
            {isIframeVersion && (
              <TextV2 typographyVariant="label1" fontWeight={800} marginTop="16px">
                {tAdmin.requiredFields()}
              </TextV2>
            )}
            <TermsAndConditions />
            <ButtonV2
              marginTop="32px"
              onClick={handleOnSaveDetailsClick}
              type="submit"
              fullWidth
              disabled={!isFormValid || formik.values.contacts.some((_, index) => isPhoneNumberInvalid(index)) || (!isIframeVersion && !formik.values.contacts[0].email)}
            >
              {tAdmin.saveDetails()}
            </ButtonV2>
          </form>
        </FormikProvider>
        {hasMutationError && (
          <TextV2 typographyVariant="body3" color="red" marginTop="16px">
            {tAdmin.contactError()}
          </TextV2>
        )}
        {!isIframeVersion && <Footer />}
      </Flex>
    </Wrapper>
  );
};
